// 1. React and core hooks
import { useContext, useEffect, useRef } from 'react';

// 2. Internal components
import { WebSocketContext } from '~/contexts/WebSocketContext';

// 3. Type imports
import type { WebSocketEventHandler } from '~/contexts/WebSocketContext';

export interface UseWebSocketSubscriptionReturn {
  /** Whether WebSocket is currently connected */
  isConnected: boolean;
}

/**
 * Custom hook for subscribing to WebSocket events without creating new connections.
 *
 * Uses the global WebSocket connection from WebSocketProvider via context.
 * Handles subscription/unsubscription lifecycle and callback reference stability.
 *
 * Per §TS.5.2: Refs avoid stale closures and prevent unnecessary re-subscriptions.
 * Throws error if used outside WebSocketProvider.
 *
 * @param onMessage - Callback invoked when WebSocket events are received
 * @returns Object containing connection state
 *
 * @example
 * ```tsx
 * function OnboardingStep() {
 *   const { isConnected } = useWebSocketSubscription((event) => {
 *     if (event.type === 'onboarding.connection_verified') {
 *       setConnectionState('success');
 *     }
 *   });
 *
 *   return <div>Connected: {isConnected}</div>;
 * }
 * ```
 */
export function useWebSocketSubscription(
  onMessage: WebSocketEventHandler
): UseWebSocketSubscriptionReturn {
  const context = useContext(WebSocketContext);

  // Enforce provider usage with descriptive error
  if (context === undefined) {
    throw new Error('useWebSocketSubscription must be used within a WebSocketProvider');
  }

  const { isConnected, subscribe } = context;

  // Per §TS.5.2: Store callback in ref to avoid re-subscription on callback change
  const onMessageRef = useRef(onMessage);

  // Keep ref in sync with latest callback
  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  // Subscribe to WebSocket events on mount, unsubscribe on unmount
  useEffect(() => {
    // Stable wrapper that calls current ref value
    const handler: WebSocketEventHandler = (event) => {
      onMessageRef.current(event);
    };

    const unsubscribe = subscribe(handler);

    return () => {
      unsubscribe();
    };
  }, [subscribe]);

  return { isConnected };
}
